import React, { useEffect, useState } from "react";
import { api } from "../../api/api";
import {
  Button,
  Col,
  DatePicker,
  Form,
  Input,
  message,
  Row,
  Select,
  Space,
  Table,
  Typography,
} from "antd";
import { Currency } from "../../types/currency.type";
import { PiMicrosoftExcelLogoFill } from "react-icons/pi";
import { useDownloadFile } from "../../hooks/useDownloadFile";
import dayjs from "dayjs";
import { roundToThreeDecimals } from "../../utils/roundToThreeDecimals";
import { IDeliveryExpense } from "../../types/deliveryExpense.type";
import { useQueryParams } from "../../hooks/useQueryParams";

const { Title } = Typography;
const { Search } = Input;
const { Option } = Select;
const { RangePicker } = DatePicker;

export default function DeliveryExpenses() {
  const queryParams = useQueryParams();
  
  const [deliveryExpenses, setDeliveryExpenses] = useState<IDeliveryExpense[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  
  // Инициализация состояний из URL query параметров
  const [page, setPage] = useState<number>(queryParams.getParamAsNumber("page", 1));
  const [pageSize, setPageSize] = useState<number>(queryParams.getParamAsNumber("pageSize", 10));
  const [total, setTotal] = useState<number>(0);
  const [searchTerm, setSearchTerm] = useState<string>(queryParams.getParam("search") || "");
  const [currency, setCurrency] = useState<Currency | undefined>(queryParams.getParam("currency") as Currency || undefined);
  
  // Инициализация dateRange из URL
  const initDateRange = (): [dayjs.Dayjs, dayjs.Dayjs] | undefined => {
    const startDate = queryParams.getParam("startDate");
    const endDate = queryParams.getParam("endDate");
    if (startDate && endDate) {
      return [dayjs(startDate, "DD.MM.YY"), dayjs(endDate, "DD.MM.YY")];
    }
    return [dayjs().startOf("month"), dayjs().endOf("month")];
  };
  
  const [dateRange, setDateRange] = useState<[dayjs.Dayjs, dayjs.Dayjs] | undefined>(initDateRange());
  const { loading: downloadLoading, downloadFile } = useDownloadFile();

  const handleRangeChange = (dates: any) => {
    if (dates) {
      setDateRange([dates[0], dates[1]]);
    } else {
      setDateRange(undefined);
    }
  };

  const handleExport = () => {
    const exportParams: { [key: string]: any } = {
      startDate: dateRange ? dateRange[0]?.format("DD.MM.YY") : undefined,
      endDate: dateRange ? dateRange[1]?.format("DD.MM.YY") : undefined,
    };

    downloadFile(`/export/delivery-expenses`, "delivery_expenses_data.xlsx", exportParams);
  };

  // Синхронизация состояний с URL query параметрами
  useEffect(() => {
    const params: Record<string, string | number | boolean | undefined> = {};
    
    if (searchTerm) params.search = searchTerm;
    if (currency) params.currency = currency;
    if (page !== 1) params.page = page;
    if (pageSize !== 10) params.pageSize = pageSize;
    if (dateRange) {
      params.startDate = dateRange[0]?.format("DD.MM.YY");
      params.endDate = dateRange[1]?.format("DD.MM.YY");
    }
    
    queryParams.setParams(params);
  }, [searchTerm, currency, page, pageSize, dateRange]);

  useEffect(() => {
    fetchDeliveryExpenses();
  }, [page, pageSize, dateRange, searchTerm, currency]);

  const fetchDeliveryExpenses = async () => {
    setLoading(true);

    try {
      const response = await api.get("/delivery-expense", {
        params: {
          page,
          limit: pageSize,
          startDate: dateRange ? dateRange[0]?.format("DD.MM.YY") : undefined,
          endDate: dateRange ? dateRange[1]?.format("DD.MM.YY") : undefined,
          currency,
        },
      });
      setDeliveryExpenses(response.data.deliveryExpenses);
      setTotal(response.data.total);
    } catch (error) {
      console.error("Error fetching delivery expenses", error);
      message.error("Ошибка при получение расходов на доставку");
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = (value: string) => {
    setSearchTerm(value);
    setPage(1);
  };

  const columns = [
    {
      title: "Дата",
      dataIndex: "date",
      key: "date",
    },
    {
      title: "Чек",
      dataIndex: "receipt",
      key: "receipt",
    },
    {
      title: "Дата заказа",
      dataIndex: "orderDate",
      key: "orderDate",
    },
    {
      title: "Клиент",
      dataIndex: "orderClientName",
      key: "orderClientName",
    },
    {
      title: "Товар/Материал",
      dataIndex: "orderItemName",
      key: "orderItemName",
    },
    {
      title: "Валюта",
      dataIndex: "currency",
      key: "currency",
    },
    {
      title: "Сумма",
      dataIndex: "amount",
      key: "amount",
      render: (amount: number) => roundToThreeDecimals(amount),
    },
    {
      title: "Курс",
      dataIndex: "exchangeRate",
      key: "exchangeRate",
    },
    {
      title: "Цена до в USD",
      key: "priceBefore",
      render: (_: any, record: IDeliveryExpense) => roundToThreeDecimals(record.priceBefore.USD),
    },
    {
      title: "Цена до в UZS",
      key: "priceBefore",
      render: (_: any, record: IDeliveryExpense) => roundToThreeDecimals(record.priceBefore.UZS),
    },
    {
      title: "Цена после в USD",
      key: "priceAfter",
      render: (_: any, record: IDeliveryExpense) => roundToThreeDecimals(record.priceAfter.USD),
    },
    {
      title: "Цена после в UZS",
      key: "priceAfter",
      render: (_: any, record: IDeliveryExpense) => roundToThreeDecimals(record.priceAfter.UZS),
    },
    {
      title: "Касса",
      dataIndex: "cashboxName",
      key: "cashboxName",
    },
    {
      title: "Описание",
      key: "orderDescription",
      render: (_: any, record: IDeliveryExpense) => record.orderDescription || record.description,
    },
  ];

  return (
    <div style={{ padding: 24 }}>
      <Title>Расходы на доставку</Title>
      <Space style={{ marginBottom: 16, marginTop: 16 }} wrap>
        <Search placeholder="Поиск" onSearch={handleSearch} allowClear />
        <Select
          placeholder="Фильтр по валюте"
          onChange={(value) => setCurrency(value)}
          allowClear
          style={{ width: 200 }}
        >
          {Object.values(Currency).map((currency) => (
            <Option key={currency} value={currency}>
              {currency}
            </Option>
          ))}
        </Select>

        <RangePicker
          onChange={handleRangeChange}
          placeholder={["Начало", "Конец"]}
          format="DD.MM.YY"
          value={dateRange}
        />

        <Button
          type="default"
          loading={downloadLoading}
          icon={<PiMicrosoftExcelLogoFill />}
          onClick={handleExport}
        >
          Экспорт в Excel
        </Button>
      </Space>
      <Table
        columns={columns}
        dataSource={deliveryExpenses}
        rowKey={(record) => record._id}
        pagination={{
          current: page,
          total: total,
          pageSize: pageSize,
          onChange: (page, size) => {
            setPage(page);
            if (size !== pageSize) setPageSize(size!);
          },
          showSizeChanger: true,
        }}
        scroll={{ x: "max-content" }}
        loading={loading}
      />
    </div>
  );
}